// AdminContent — gestion des RÉFÉRENCES (carousel Expérience) et des AVIS clients (v5.22.0).
// Contenus administrables par le cabinet : ajouter · masquer/afficher · supprimer.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { T } from "./cabinetTheme";
import { getFounderToken } from "@/lib/founderApi";

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${getFounderToken()}` });

export const AdminContent = ({ slug }) => {
  const [refs, setRefs] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [refForm, setRefForm] = useState({ domain: "", title: "", description: "" });
  const [revForm, setRevForm] = useState({ author: "", rating: 5, text: "", when_label: "" });

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/api/v3/cabinet/${slug}/admin/references`, { headers: hdr() }),
        axios.get(`${API}/api/v3/cabinet/${slug}/admin/reviews`, { headers: hdr() }),
      ]);
      setRefs(r1.data.references); setReviews(r2.data.reviews);
    } catch { toast.error("Chargement impossible"); }
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  const patch = (kind, id) => async (fields) => {
    try { await axios.patch(`${API}/api/v3/cabinet/${slug}/admin/${kind}/${id}`, fields, { headers: hdr() }); load(); }
    catch { toast.error("Échec"); }
  };
  const del = (kind, id) => async () => {
    try { await axios.delete(`${API}/api/v3/cabinet/${slug}/admin/${kind}/${id}`, { headers: hdr() }); toast.success("Supprimé"); load(); }
    catch { toast.error("Échec"); }
  };
  const addRef = async () => {
    if (!refForm.domain || !refForm.title) { toast.error("Domaine et titre requis"); return; }
    try { await axios.post(`${API}/api/v3/cabinet/${slug}/admin/references`, refForm, { headers: hdr() }); setRefForm({ domain: "", title: "", description: "" }); toast.success("Référence ajoutée"); load(); }
    catch { toast.error("Échec"); }
  };
  const addRev = async () => {
    if (!revForm.author || !revForm.text) { toast.error("Auteur et texte requis"); return; }
    try { await axios.post(`${API}/api/v3/cabinet/${slug}/admin/reviews`, revForm, { headers: hdr() }); setRevForm({ author: "", rating: 5, text: "", when_label: "" }); toast.success("Avis ajouté"); load(); }
    catch { toast.error("Échec"); }
  };

  const inp = { ...T.input, borderRadius: 9, padding: "8px 11px", fontSize: 12 };
  const miniBtn = (label, onClick, danger) => (
    <button onClick={onClick} style={{ padding: "4px 10px", borderRadius: 8, border: `1px solid ${danger ? "#FCA5A5" : "#E5E7EB"}`, background: "#fff", color: danger ? "#DC2626" : "#374151", fontSize: 10.5, fontWeight: 700, cursor: "pointer" }}>{label}</button>
  );

  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="admin-content">
      <div style={T.card} data-testid="admin-references">
        <h2 style={T.h2}>Références — « L'expérience du cabinet »</h2>
        <p style={{ ...T.sub, fontSize: 11.5, margin: "3px 0 8px" }}>Visibles sur la page du cabinet. Ne publiez que des contenus validés par le cabinet — jamais de résultats judiciaires inventés.</p>
        {refs.map((r) => (
          <div key={r.reference_id} style={{ padding: "9px 0", borderBottom: "1px solid #F3F4F6", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} data-testid={`admin-ref-${r.reference_id}`}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{r.title} {r.is_demo && <span style={{ ...T.badge, ...T.badgeDemo }}>DEMO</span>} {r.active === false && <span style={{ ...T.badge, background: "#F3F4F6", color: "#6B7280" }}>Masquée</span>}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{r.domain} · {r.description}</div>
            </div>
            {miniBtn(r.active === false ? "Afficher" : "Masquer", () => patch("references", r.reference_id)({ active: r.active === false }))}
            {miniBtn("Supprimer", del("references", r.reference_id), true)}
          </div>
        ))}
        <div style={{ display: "grid", gap: 7, marginTop: 10 }}>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            <input style={{ ...inp, flex: 1, minWidth: 130 }} placeholder="Domaine (ex: Droit immobilier)" value={refForm.domain} onChange={(e) => setRefForm({ ...refForm, domain: e.target.value })} data-testid="ref-domain-input" />
            <input style={{ ...inp, flex: 1.4, minWidth: 150 }} placeholder="Titre" value={refForm.title} onChange={(e) => setRefForm({ ...refForm, title: e.target.value })} data-testid="ref-title-input" />
          </div>
          <input style={inp} placeholder="Courte description" value={refForm.description} onChange={(e) => setRefForm({ ...refForm, description: e.target.value })} data-testid="ref-desc-input" />
          <button style={{ ...T.btnGreen, padding: "9px 14px", fontSize: 12, justifySelf: "start" }} onClick={addRef} data-testid="ref-add-btn">+ Ajouter une référence</button>
        </div>
      </div>

      <div style={T.card} data-testid="admin-reviews">
        <h2 style={T.h2}>Avis de nos clients</h2>
        <p style={{ ...T.sub, fontSize: 11.5, margin: "3px 0 8px" }}>N'ajoutez que des avis réellement autorisés par vos clients. Les avis DEMO sont identifiés comme tels côté public.</p>
        {reviews.map((r) => (
          <div key={r.review_id} style={{ padding: "9px 0", borderBottom: "1px solid #F3F4F6", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }} data-testid={`admin-review-${r.review_id}`}>
            <div style={{ flex: 1, minWidth: 160 }}>
              <div style={{ fontSize: 12.5, fontWeight: 800 }}>{r.author} <span style={{ color: "#F59E0B" }}>{"★".repeat(r.rating)}</span> {r.is_demo && <span style={{ ...T.badge, ...T.badgeDemo }}>DEMO</span>} {r.active === false && <span style={{ ...T.badge, background: "#F3F4F6", color: "#6B7280" }}>Masqué</span>}</div>
              <div style={{ ...T.sub, fontSize: 11 }}>{r.text}</div>
            </div>
            {miniBtn(r.active === false ? "Afficher" : "Masquer", () => patch("reviews", r.review_id)({ active: r.active === false }))}
            {miniBtn("Supprimer", del("reviews", r.review_id), true)}
          </div>
        ))}
        <div style={{ display: "grid", gap: 7, marginTop: 10 }}>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder="Auteur (ex: K. N'Guessan)" value={revForm.author} onChange={(e) => setRevForm({ ...revForm, author: e.target.value })} data-testid="review-author-input" />
            <select style={{ ...inp, width: 92 }} value={revForm.rating} onChange={(e) => setRevForm({ ...revForm, rating: Number(e.target.value) })} data-testid="review-rating-select">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
            </select>
            <input style={{ ...inp, width: 130 }} placeholder="Quand (ex: Il y a 1 mois)" value={revForm.when_label} onChange={(e) => setRevForm({ ...revForm, when_label: e.target.value })} data-testid="review-when-input" />
          </div>
          <input style={inp} placeholder="Texte de l'avis" value={revForm.text} onChange={(e) => setRevForm({ ...revForm, text: e.target.value })} data-testid="review-text-input" />
          <button style={{ ...T.btnGreen, padding: "9px 14px", fontSize: 12, justifySelf: "start" }} onClick={addRev} data-testid="review-add-btn">+ Ajouter un avis</button>
        </div>
      </div>
    </div>
  );
};
